// components/booking/StripeCheckoutForm.js
import React, { useState, useEffect } from 'react';
import { 
  CardElement, 
  useStripe, 
  useElements,
  AddressElement,
} from '@stripe/react-stripe-js';
import { 
  ArrowLeft, 
  Lock, 
  AlertTriangle, 
  CheckCircle, 
  Download, 
  Calendar, 
  Clock, 
  Car, 
  Receipt, 
  User,
  ArrowRight,
  Home,
  CreditCard
} from 'lucide-react';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { downloadBookingReceipt } from '../../utils/pdfGenerator';

const StripeCheckoutForm = ({ 
  onSuccess,
  onBack,
  serviceDetails,
  schedulingDetails,
  vehicleDetails,
  customerInfo,
  paymentDetails
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('error'); // 'error', 'warning', or 'info'
  const [paymentStatus, setPaymentStatus] = useState('initial'); // 'initial', 'processing', 'succeeded', 'failed'
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [useSavedMethod, setUseSavedMethod] = useState(false);
  const [selectedSavedMethodId, setSelectedSavedMethodId] = useState(null);
  const [billingDetails, setBillingDetails] = useState({});
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Added for name validation
  const [billingName, setBillingName] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameComplete, setNameComplete] = useState(false);

  // Fetch user's saved payment methods if logged in
  useEffect(() => {
    const fetchSavedPaymentMethods = async () => {
      if (user) {
        try {
          const userPaymentMethodsRef = ref(database, `users/${user.uid}/paymentMethods`);
          const snapshot = await get(userPaymentMethodsRef);
          
          if (snapshot.exists()) {
            const methods = snapshot.val();
            const methodsArray = Object.values(methods);
            setSavedPaymentMethods(methodsArray);
            
            // Set default method if available
            const defaultMethod = methodsArray.find(method => method.isDefault);
            if (defaultMethod) {
              setSelectedSavedMethodId(defaultMethod.paymentMethodId);
            }
          }
        } catch (error) {
          console.error('Error fetching saved payment methods:', error);
        }
      }
    };
    
    fetchSavedPaymentMethods();
    
    // Also fetch user profile for billing address if available
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const userProfileRef = ref(database, `users/${user.uid}/profile`);
          const snapshot = await get(userProfileRef);
          
          if (snapshot.exists()) {
            const profile = snapshot.val();
            if (profile) {
              const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
              setBillingDetails({
                name: fullName,
                email: user.email || '',
                address: {
                  line1: profile.address || '',
                  city: profile.city || '',
                  state: profile.state || '',
                  postal_code: profile.zipCode || ''
                }
              });
              
              // Set billing name from profile if it exists
              if (fullName) {
                setBillingName(fullName);
                validateName(fullName);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    
    fetchUserProfile();
  }, [user]);

  // Validate the name field
  const validateName = (name) => {
    const nameToCheck = name || billingName;
    
    if (!nameToCheck.trim()) {
      setNameError('Please provide your full name');
      setNameComplete(false);
      return false;
    }
    
    // Check if name has at least first and last name
    const nameParts = nameToCheck.trim().split(' ');
    if (nameParts.length < 2 || !nameParts[1] || nameParts[1].trim() === '') {
      setNameError('Please provide both your first and last name');
      setNameComplete(false);
      return false;
    }
    
    setNameError('');
    setNameComplete(true);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      setMessage('Stripe is still loading. Please wait a moment and try again.');
      setMessageType('warning');
      return;
    }

    // Validate name field if not using saved method
    if (!useSavedMethod && !validateName()) {
      return;
    }

    setIsLoading(true);
    setMessage(null);
    setPaymentStatus('processing');

    let result;
    
    try {
      if (useSavedMethod && selectedSavedMethodId) {
        // For saved payment methods, we need to get the client secret first
        // This would typically come from your payment intent creation
        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: paymentDetails?.total || serviceDetails?.price || 0,
            currency: 'usd',
            payment_method: selectedSavedMethodId,
            metadata: {
              service: serviceDetails?.title || 'Auto Detailing Service',
              customer: billingName || customerInfo?.name || (user ? user.displayName : 'Guest'),
            },
          }),
        });

        const { clientSecret } = await response.json();

        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: selectedSavedMethodId
        });
      } else {
        // Using CardElement - get the card element
        const cardElement = elements.getElement(CardElement);
        
        if (!cardElement) {
          setMessage('Card element not found. Please refresh the page and try again.');
          setMessageType('error');
          setIsLoading(false);
          setPaymentStatus('failed');
          return;
        }

        // First, create the payment intent
        const response = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: paymentDetails?.total || serviceDetails?.price || 0,
            currency: 'usd',
            metadata: {
              service: serviceDetails?.title || 'Auto Detailing Service',
              customer: billingName || customerInfo?.name || (user ? user.displayName : 'Guest'),
            },
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const { clientSecret } = await response.json();

        // Confirm payment with CardElement
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: billingName,
            },
          }
        });
      }
    } catch (error) {
      console.error('Payment confirmation error:', error);
      
      // Handle specific validation errors
      if (error.code === 'incomplete_name') {
        setNameError('Please provide your full name');
        setIsLoading(false);
        setPaymentStatus('failed');
        return;
      }
      
      setMessage(error.message || 'An unexpected error occurred.');
      setMessageType('error');
      setIsLoading(false);
      setPaymentStatus('failed');
      return;
    }

    if (result.error) {
      // Show error to your customer (e.g., insufficient funds)
      setMessage(result.error.message);
      setMessageType('error');
      setIsLoading(false);
      setPaymentStatus('failed');
    } else {
      // The payment has been processed!
      if (result.paymentIntent.status === 'succeeded') {
        setPaymentStatus('succeeded');
        setPaymentIntent(result.paymentIntent);
        setMessage('Payment successful! Your booking has been confirmed.');
        setMessageType('info');
        
        // Call the success callback
        if (onSuccess) {
          onSuccess(result.paymentIntent);
        }
      }
      setIsLoading(false);
    }
  };

  // Get message styling based on type
  const getMessageStyles = () => {
    switch (messageType) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-600';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-600';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-600';
      default:
        return 'bg-red-50 border-red-200 text-red-600';
    }
  };

  // Generate and download PDF receipt
  const generatePDF = () => {
    if (!paymentIntent) return;
    
    try {
      setGeneratingPdf(true);
      
      // Prepare receipt data from current state
      const receiptData = {
        id: paymentIntent.id || `payment-${Date.now()}`,
        date: new Date().toISOString(),
        service: serviceDetails,
        customer: {
          name: billingName || (customerInfo?.name || (user ? user.displayName : "Guest")),
          email: customerInfo?.email || (user ? user.email : "")
        },
        vehicle: vehicleDetails?.vehicle ? {
          make: vehicleDetails.vehicle.make || '',
          model: vehicleDetails.vehicle.model || '',
          year: vehicleDetails.vehicle.year || ''
        } : {},
        scheduling: schedulingDetails,
        payment: {
          subtotal: paymentDetails?.subtotal || (serviceDetails?.price || 0),
          tax: paymentDetails?.tax || ((paymentDetails?.subtotal || (serviceDetails?.price || 0)) * 0.0825),
          total: paymentDetails?.total || ((paymentDetails?.subtotal || (serviceDetails?.price || 0)) + (paymentDetails?.tax || 0)),
          last4: paymentIntent?.payment_method_details?.card?.last4 || '****'
        }
      };
      
      // Use our PDF generator utility to download the receipt
      const success = downloadBookingReceipt(receiptData);
      
      if (!success) {
        throw new Error("Failed to generate PDF");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("There was a problem generating your receipt. Please try again later.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // If payment succeeded, show the receipt
  if (paymentStatus === 'succeeded') {
    // Calculate payment details if not provided
    const total = paymentDetails?.total || (serviceDetails?.price || 0);
    const subtotal = paymentDetails?.subtotal || (serviceDetails?.price || 0);
    const tax = paymentDetails?.tax || (subtotal * 0.0825); // Default 8.25% tax
    
    // Get last 4 digits of card
    const last4 = paymentIntent?.payment_method_details?.card?.last4 || '****';

    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-800">Booking Confirmed!</h1>
                <p className="text-gray-600">Your payment has been processed successfully.</p>
              </div>
            </div>
            <button
              onClick={generatePDF}
              disabled={generatingPdf}
              className="flex items-center text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingPdf ? (
                <>
                  <div className="animate-spin h-5 w-5 border-b-2 border-blue-600 rounded-full mr-1"></div>
                  <span>Generating PDF...</span>
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-1" />
                  <span>Download PDF</span>
                </>
              )}
            </button>
          </div>

          {/* Receipt Header */}
          <div className="border-b border-gray-200 pb-4 mb-6">
            <div className="flex justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-700">Receipt</h2>
                <p className="text-gray-500 text-sm">
                  {paymentIntent?.id && `Booking ID: ${paymentIntent.id.slice(-8)}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-gray-700">
                  {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="mb-6">
            <div className="flex items-start">
              <User className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-700">Customer Information</h3>
                <p className="text-gray-600">
                  {billingName || customerInfo?.name || (user ? user.displayName : "Guest")}
                </p>
                <p className="text-gray-600">
                  {customerInfo?.email || (user ? user.email : "")}
                </p>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-700 mb-3 flex items-center">
              <Receipt className="h-5 w-5 text-blue-500 mr-2" />
              Service Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Service */}
              <div>
                <h4 className="text-sm text-gray-500">Service</h4>
                <p className="font-medium">{serviceDetails?.title || 'Service'}</p>
              </div>

              {/* Vehicle Details */}
              <div>
                <h4 className="text-sm text-gray-500 flex items-center">
                  <Car className="h-4 w-4 mr-1 text-gray-400" />
                  Vehicle
                </h4>
                <p className="font-medium">
                  {vehicleDetails?.vehicle ? 
                    `${vehicleDetails.vehicle.year} ${vehicleDetails.vehicle.make} ${vehicleDetails.vehicle.model}` :
                    'Not specified'}
                </p>
              </div>

              {/* Appointment Date */}
              <div>
                <h4 className="text-sm text-gray-500 flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                  Date
                </h4>
                <p className="font-medium">
                  {schedulingDetails?.date ? formatDate(schedulingDetails.date) : 'Not scheduled'}
                </p>
              </div>

              {/* Appointment Time */}
              <div>
                <h4 className="text-sm text-gray-500 flex items-center">
                  <Clock className="h-4 w-4 mr-1 text-gray-400" />
                  Time
                </h4>
                <p className="font-medium">{schedulingDetails?.time || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="border-t border-gray-200 pt-4 mb-6">
            <h3 className="font-medium text-gray-700 mb-3">Payment Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-medium text-lg pt-2 border-t border-gray-200">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <div className="text-right text-sm text-gray-500">
                {paymentIntent?.payment_method_type === 'card' ? (
                  `Paid with card ending in ${last4}`
                ) : (
                  `Paid with ${paymentIntent?.payment_method_type || 'selected payment method'}`
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <a
              href="/"
              className="flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300"
            >
              <Home className="h-5 w-5 mr-2" />
              Return to Home
            </a>
            <a
              href="/"
              className="flex items-center justify-center bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300"
              onClick={(e) => {
                e.preventDefault();
                onSuccess(paymentIntent);
                window.location.href = '/';
              }}
            >
              Return Home <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Render saved payment methods if available
  const renderSavedPaymentMethods = () => {
    if (!user || savedPaymentMethods.length === 0) return null;
    
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold mb-3">Saved Payment Methods</h3>
          <button 
            type="button"
            onClick={() => setUseSavedMethod(false)}
            className="text-sm text-blue-600 hover:underline"
          >
            {useSavedMethod ? 'Use new payment method' : ''}
          </button>
        </div>
        
        {useSavedMethod ? (
          <div className="space-y-3">
            {savedPaymentMethods.map(method => (
              <div 
                key={method.paymentMethodId || method.id}
                className={`border rounded-lg p-3 cursor-pointer ${
                  selectedSavedMethodId === (method.paymentMethodId || method.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200'
                }`}
                onClick={() => setSelectedSavedMethodId(method.paymentMethodId || method.id)}
              >
                <div className="flex items-center">
                  <div className="mr-3">
                    <CreditCard className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-grow">
                    <p className="font-medium capitalize">
                      {method.brand} •••• {method.last4}
                    </p>
                    <p className="text-sm text-gray-500">
                      Expires {method.expMonth?.toString().padStart(2, '0') || '??'}/{(method.expYear?.toString() || '??').slice(-2)}
                    </p>
                  </div>
                  <div className="ml-2">
                    <div className={`h-5 w-5 rounded-full border ${
                      selectedSavedMethodId === (method.paymentMethodId || method.id)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    } flex items-center justify-center`}>
                      {selectedSavedMethodId === (method.paymentMethodId || method.id) && (
                        <div className="h-2 w-2 bg-white rounded-full"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setUseSavedMethod(true);
              // Select the default or first payment method
              const defaultMethod = savedPaymentMethods.find(m => m.isDefault) || savedPaymentMethods[0];
              if (defaultMethod) {
                setSelectedSavedMethodId(defaultMethod.paymentMethodId || defaultMethod.id);
              }
            }}
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
          >
            Use a saved payment method ({savedPaymentMethods.length} available)
          </button>
        )}
      </div>
    );
  };

  // Normal payment form display
  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      {/* Show any errors or messages */}
      {message && (
        <div className={`p-4 mb-4 border rounded-lg flex items-start ${getMessageStyles()}`}>
          <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{message}</span>
        </div>
      )}
      
      {/* Saved Payment Methods */}
      {renderSavedPaymentMethods()}
      
      {/* Only show the new payment form if not using a saved method */}
      {!useSavedMethod && (
        <>
          {/* Name Field - Added to fix validation error */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name on Card <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={billingName}
              onChange={(e) => {
                setBillingName(e.target.value);
                if (nameError) validateName(e.target.value);
              }}
              onBlur={() => validateName()}
              placeholder="John Doe"
              className={`w-full px-3 py-2 border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500`}
              required
            />
            {nameError && (
              <p className="mt-1 text-sm text-red-600">{nameError}</p>
            )}
          </div>

          {/* Card Element - Simple card input */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Payment Method</h3>
            <div className="border border-gray-300 rounded-md p-3">
              <CardElement 
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                  hidePostalCode: true, // We'll use AddressElement for billing address
                }}
              />
            </div>
          </div>
          
          {/* Billing Address */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Billing Address</h3>
            <AddressElement 
              options={{
                mode: 'billing',
                defaultValues: billingDetails,
                fields: {
                  phone: 'always',
                },
                validation: {
                  phone: {
                    required: 'never'
                  }
                }
              }}
            />
          </div>
        </>
      )}
      
      <div className="mt-6 flex justify-between">
        <button
          type="button"
          className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 flex items-center"
          onClick={onBack}
          disabled={isLoading || paymentStatus === 'processing'}
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back
        </button>
        
        <button
          type="submit"
          className={`bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
            !stripe || isLoading || paymentStatus === 'processing' || (!useSavedMethod && !nameComplete) ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-lg'
          }`}
          disabled={!stripe || isLoading || paymentStatus === 'processing' || (!useSavedMethod && !nameComplete)}
        >
          {isLoading || paymentStatus === 'processing' ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : 'Complete Payment'}
        </button>
      </div>
      
      <div className="text-sm text-gray-500 mt-4 flex items-start">
        <Lock className="h-4 w-4 mr-2 mt-0.5 text-gray-400" />
        Your payment is processed securely through Stripe.
      </div>
    </form>
  );
};

export default StripeCheckoutForm;